import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  cancelSubscription,
  changeSubscriptionPlan,
  createBillingPortalSession,
  createSubscriptionCheckout,
  getBillingLedger,
  getCreditLedger,
  getOverviewData,
  getSubscription,
  getSubscriptionPlans,
  resumeSubscription,
} from '../../api';

const ACTIVE_STATUSES = new Set(['active', 'trialing', 'past_due', 'unpaid']);

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function formatMoney(value) {
  const number = Number(value || 0);
  return `$${number.toFixed(2)}`;
}

function entitlementText(entitlements) {
  if (!entitlements || Object.keys(entitlements).length === 0) return 'Standard platform access';
  return Object.entries(entitlements)
    .map(([key, value]) => `${key.replaceAll('_', ' ')}: ${Array.isArray(value) ? value.join(', ') : String(value)}`)
    .join(' · ');
}

export default function Billing() {
  const token = localStorage.getItem('authToken');
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [creditLedger, setCreditLedger] = useState([]);
  const [usageLedger, setUsageLedger] = useState([]);
  const [creditBalance, setCreditBalance] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadBilling = useCallback(async () => {
    if (!token) {
      window.location.href = '/login';
      return;
    }
    setLoading(true);
    setError('');
    try {
      const results = await Promise.all([
        getSubscriptionPlans(token),
        getSubscription(token),
        getCreditLedger(token),
        getBillingLedger(token),
        getOverviewData(token, '7d'),
      ]);
      const [planData, subscriptionData, creditData, usageData, overviewData] = results;
      setPlans(planData.plans || []);
      setSubscription(subscriptionData.subscription || null);
      setCreditLedger(creditData.credits || []);
      setUsageLedger(usageData.ledger?.length ? usageData.ledger : (usageData.usage || []));
      setCreditBalance(Number(overviewData.credits || 0));
    } catch (err) {
      setError(err.message || 'Unable to load billing details.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  const hasManagedSubscription = Boolean(subscription?.stripe_subscription_id);
  const isActive = hasManagedSubscription && ACTIVE_STATUSES.has(subscription?.status);
  const currentPlan = useMemo(
    () => plans.find((plan) => plan.plan_id === subscription?.plan_id),
    [plans, subscription],
  );

  const runAction = async (name, callback, successMessage) => {
    setAction(name);
    setError('');
    setNotice('');
    try {
      const result = await callback();
      if (result?.url) {
        window.location.assign(result.url);
        return;
      }
      if (result?.subscription) setSubscription(result.subscription);
      setNotice(successMessage);
    } catch (err) {
      setError(err.message || 'Billing action failed.');
    } finally {
      setAction('');
    }
  };

  const handlePlanAction = () => {
    if (!selectedPlan) return;
    if (isActive) {
      runAction('plan', () => changeSubscriptionPlan(token, selectedPlan), 'Subscription plan updated.');
    } else {
      runAction('checkout', () => createSubscriptionCheckout(token, selectedPlan), 'Redirecting to checkout.');
    }
  };

  if (loading) {
    return <div className="bg-white rounded-xl border border-slate-200 p-8 text-slate-600">Loading billing…</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-800">Billing</h2>
        <p className="mt-1 text-sm text-slate-500">Manage your platform subscription and usage credits in one place.</p>
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
          {error.toLowerCase().includes('credit') || error.toLowerCase().includes('entitlement') ? (
            <p className="mt-1 font-medium">Add credits or choose a plan with the required access, then retry.</p>
          ) : null}
        </div>
      )}
      {notice && <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700">{notice}</div>}

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Current subscription</h3>
            <p className="mt-2 text-sm text-slate-600">
              Plan: <strong>{subscription?.plan_id || 'Not selected'}</strong> · Status: <strong>{subscription?.status || 'unknown'}</strong>
            </p>
            <p className="mt-1 text-sm text-slate-600">Available credits: <strong>{formatMoney(creditBalance)}</strong></p>
            <p className="mt-1 text-sm text-slate-500">{entitlementText(subscription?.entitlements)}</p>
            {subscription?.current_period_end && (
              <p className="mt-1 text-xs text-slate-500">
                Current period ends {formatDate(subscription.current_period_end)}
                {subscription.cancel_at_period_end ? ' · Cancels at period end' : ''}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {hasManagedSubscription && (
              <button
                type="button"
                onClick={() => runAction('portal', () => createBillingPortalSession(token), 'Redirecting to the Stripe billing portal.')}
                disabled={Boolean(action)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                {action === 'portal' ? 'Opening…' : 'Manage payment method'}
              </button>
            )}
            {isActive && !subscription.cancel_at_period_end && (
              <button
                type="button"
                onClick={() => runAction('cancel', () => cancelSubscription(token), 'Cancellation scheduled for the end of the current period.')}
                disabled={Boolean(action)}
                className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
              >
                {action === 'cancel' ? 'Updating…' : 'Cancel at period end'}
              </button>
            )}
            {isActive && subscription.cancel_at_period_end && (
              <button
                type="button"
                onClick={() => runAction('resume', () => resumeSubscription(token), 'Subscription resumed.')}
                disabled={Boolean(action)}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                {action === 'resume' ? 'Updating…' : 'Resume subscription'}
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Subscription plans</h3>
            <p className="mt-1 text-sm text-slate-500">Plans and entitlements are supplied by the billing service.</p>
          </div>
          <div className="flex gap-2">
            <select
              aria-label="Subscription plan"
              value={selectedPlan}
              onChange={(event) => setSelectedPlan(event.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">Choose a plan</option>
              {plans.map((plan) => <option key={plan.plan_id} value={plan.plan_id}>{plan.name}</option>)}
            </select>
            <button
              type="button"
              onClick={handlePlanAction}
              disabled={!selectedPlan || Boolean(action)}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {action === 'checkout' ? 'Opening…' : isActive ? 'Change plan' : 'Subscribe'}
            </button>
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {plans.map((plan) => (
            <button
              type="button"
              key={plan.plan_id}
              onClick={() => setSelectedPlan(plan.plan_id)}
              className={`rounded-lg border p-4 text-left transition-colors ${selectedPlan === plan.plan_id || currentPlan?.plan_id === plan.plan_id ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 hover:border-indigo-200'}`}
            >
              <div className="font-semibold text-slate-800">{plan.name}</div>
              <div className="mt-1 text-xs text-slate-500">{entitlementText(plan.entitlements)}</div>
            </button>
          ))}
          {!plans.length && <p className="text-sm text-slate-500">No subscription plans are currently configured.</p>}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-800">Credit ledger</h3>
          <p className="mt-1 text-sm text-slate-500">Purchases are fulfilled by the Stripe webhook.</p>
          <div className="mt-4 overflow-x-auto">
            {creditLedger.length ? (
              <table className="min-w-full text-left text-sm">
                <thead className="border-b text-xs uppercase text-slate-500"><tr><th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Added</th><th className="py-2">Payment</th></tr></thead>
                <tbody>{creditLedger.map((row) => <tr key={row.payment_intent_id} className="border-b last:border-0"><td className="py-2 pr-4 text-slate-600">{formatDate(row.timestamp)}</td><td className="py-2 pr-4 font-medium">{formatMoney(row.credits_added)}</td><td className="max-w-[10rem] truncate py-2 text-xs text-slate-500">{row.payment_intent_id || '—'}</td></tr>)}</tbody>
              </table>
            ) : <p className="mt-4 text-sm text-slate-500">No credit purchases yet.</p>}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-800">Usage billing ledger</h3>
          <p className="mt-1 text-sm text-slate-500">Authoritative usage and cost records from the platform.</p>
          <div className="mt-4 max-h-64 overflow-auto">
            {usageLedger.length ? (
              <table className="min-w-full text-left text-sm">
                <thead className="border-b text-xs uppercase text-slate-500"><tr><th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Model</th><th className="py-2">Cost</th></tr></thead>
                <tbody>{usageLedger.map((row) => <tr key={row.entry_id || row.usage_id || `${row.created_at}-${row.model}`} className="border-b last:border-0"><td className="py-2 pr-4 text-slate-600">{formatDate(row.created_at)}</td><td className="py-2 pr-4 text-slate-700">{row.model || row.entry_type || '—'}</td><td className="py-2 font-medium">{formatMoney(row.total_cost_usd ?? (Number(row.total_cost_micro_usd ?? Math.abs(row.amount_micro_usd || 0)) / 1000000))}</td></tr>)}</tbody>
              </table>
            ) : <p className="mt-4 text-sm text-slate-500">No usage charges yet.</p>}
          </div>
        </div>
      </section>
    </div>
  );
}
